import { useSnackbarContext } from "@ameelio/ui";
import { useLazyQuery } from "@apollo/client/react";
import * as Sentry from "@sentry/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { GetUploadUrlDocument } from "./GetUploadUrl.generated";

export default function useFileUpload() {
  const { t } = useTranslation();
  const snackbarContext = useSnackbarContext();
  const [getUploadUrl] = useLazyQuery(GetUploadUrlDocument, {
    fetchPolicy: "no-cache",
  });
  const [uploading, setUploading] = useState(false);

  const upload = useCallback(
    async (file: File) => {
      setUploading(true);
      try {
        const { data, error } = await getUploadUrl({
          variables: { contentType: file.type },
        });

        if (!data?.uploadUrl) {
          snackbarContext.alert(
            "error",
            t("Sorry, something went wrong. We'll do what we can to fix it.")
          );
          const err = error || new Error("Could not get upload URL");
          Sentry.captureException(err);
          throw err;
        }

        const response = await fetch(data.uploadUrl.url, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });
        if (!response.ok) {
          snackbarContext.alert(
            "error",
            t("Sorry, something went wrong. We'll do what we can to fix it.")
          );
          Sentry.captureMessage(
            `File upload failed. Code: ${response.status}, Text: ${response.statusText}`,
            "error"
          );
          throw new Error("File upload failed");
        }
        return data.uploadUrl.key;
      } finally {
        setUploading(false);
      }
    },
    [getUploadUrl, snackbarContext, t]
  );

  return { upload, uploading };
}
